import {
  getQuestions,
  getQuestionStats,
  getQuestionCategories,
  createQuestion,
  updateQuestion,
  toggleQuestionStatus,
  deleteQuestion,
  uploadQuestionImage,
  bulkImportQuestions,
} from '../../shared/services/apiService';

export const EMPTY_QUESTION = {
  question_text: '', option_a: '', option_b: '', option_c: '', option_d: '',
  correct_option: 'A', difficulty: 'medium', category: '', marks: 1,
  question_image: '', option_a_image: '', option_b_image: '', option_c_image: '', option_d_image: '',
};

// ── Load questions, stats and categories in parallel ─────────────────────────
export const loadQuestionsData = async (filters = {}) => {
  const params = {};
  if (filters.category)   params.category   = filters.category;
  if (filters.difficulty) params.difficulty = filters.difficulty;

  const [qRes, sRes, cRes] = await Promise.all([
    getQuestions(params),
    getQuestionStats(),
    getQuestionCategories(),
  ]);

  return {
    questions:  qRes.data.data.questions,
    stats:      sRes.data.data.stats,
    categories: cRes.data.data.categories,
  };
};

// ── Save (create or update) a question ───────────────────────────────────────
export const saveQuestion = async (formData, editId) => {
  if (editId) {
    await updateQuestion(editId, formData);
    return 'updated';
  }
  await createQuestion(formData);
  return 'created';
};

// ── Toggle active/inactive ────────────────────────────────────────────────────
export const toggleQuestion = async (id, isCurrentlyActive) => {
  await toggleQuestionStatus(id, { is_active: !isCurrentlyActive });
};

// ── Delete question ───────────────────────────────────────────────────────────
export const removeQuestion = async (id) => {
  await deleteQuestion(id);
};

// ── Upload an image and return its URL ───────────────────────────────────────
export const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('image', file);
  const res = await uploadQuestionImage(fd);
  return res.data.data.url;
};

// ── Split full CSV into rows (handles commas + newlines inside quoted fields) ──
const parseCSVRows = (text) => {
  let t = String(text).replace(/^\uFEFF/, '');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  const pushField = () => {
    row.push(field.trim());
    field = '';
  };
  const pushRow = () => {
    if (row.length && row.some(c => c !== '')) rows.push(row);
    row = [];
  };

  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (inQuotes) {
      if (ch === '"') {
        if (t[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === ',') { pushField(); continue; }
    if (ch === '\n') { pushField(); pushRow(); continue; }
    if (ch === '\r') {
      if (t[i + 1] === '\n') i++;
      pushField(); pushRow(); continue;
    }
    field += ch;
  }
  pushField();
  if (row.length && row.some(c => c !== '')) rows.push(row);

  return rows;
};

const normalizeCorrectOption = (raw) => {
  const s = String(raw ?? '').trim().toUpperCase();
  if (['A', 'B', 'C', 'D'].includes(s)) return s;
  const only = s.replace(/[^ABCD]/g, '');
  if (only.length === 1) return only;
  const m = String(raw ?? '').toUpperCase().match(/\b([ABCD])\b/);
  return m ? m[1] : s;
};

// ── Human-readable validation (1-based row index for messages) ──────────────
export const describeCsvRowValidationError = (r) => {
  if (!r.question_text?.trim()) return 'missing question_text';
  if (!r.option_a?.trim()) return 'missing option_a';
  if (!r.option_b?.trim()) return 'missing option_b';
  if (!r.option_c?.trim()) return 'missing option_c';
  if (!r.option_d?.trim()) return 'missing option_d';
  if (!['A', 'B', 'C', 'D'].includes(r.correct_option)) {
    const show = (r.correct_option || '').trim() || '(empty)';
    return `correct_option must be A, B, C, or D (got "${show}")`;
  }
  return null;
};

// ── Parse CSV text and return array of question objects ───────────────────────
export const parseCSVText = (text) => {
  const allRows = parseCSVRows(text);
  if (allRows.length < 2) throw new Error('CSV must have a header row and at least one data row');

  const headers = allRows[0].map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));

  const required = ['question_text', 'option_a', 'option_b', 'option_c', 'option_d', 'correct_option'];
  for (const r of required) {
    if (!headers.includes(r)) throw new Error(`Missing required column: "${r}"`);
  }

  const rows = [];
  for (let i = 1; i < allRows.length; i++) {
    const values = allRows[i];
    if (values.every(v => !v || v.trim() === '')) continue;

    const row = {};
    headers.forEach((h, idx) => { row[h] = (values[idx] || '').trim(); });

    row.correct_option = normalizeCorrectOption(row.correct_option);
    const d = (row.difficulty || '').toLowerCase().trim();
    row.difficulty = ['easy', 'medium', 'hard'].includes(d) ? d : 'medium';
    row.marks          = parseInt(row.marks, 10)      || 1;
    row.time_limit     = parseInt(row.time_limit, 10) || 60;
    row.category       = row.category || '';
    rows.push(row);
  }
  if (rows.length === 0) throw new Error('No valid data rows found in CSV');
  return rows;
};

// ── Read a File object and resolve with parsed rows ───────────────────────────
export const readAndParseCSV = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = e => { try { resolve(parseCSVText(e.target.result)); } catch (err) { reject(err); } };
    reader.onerror = ()  => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });

// ── Save bulk questions to backend ────────────────────────────────────────────
export const saveBulkQuestions = async (questions) => {
  const res = await bulkImportQuestions(questions);
  return res.data.data.count;
};

// ── CSV template download helper ──────────────────────────────────────────────
export const downloadCSVTemplate = () => {
  const header = 'question_text,option_a,option_b,option_c,option_d,correct_option,difficulty,category,marks,time_limit';
  const sample = '"What is 2 + 2?","3","4","5","6","B","easy","Math",1,60';
  const blob   = new Blob([header + '\n' + sample], { type: 'text/csv' });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement('a');
  a.href = url; a.download = 'questions_template.csv'; a.click();
  URL.revokeObjectURL(url);
};
